import { useGT } from 'gt-react';
import {
  CircleNotchIcon,
  MicrophoneIcon,
  MicrophoneSlashIcon,
  PhoneIcon,
  XIcon,
} from '@phosphor-icons/react';
import { BarVisualizer } from '@/components/ui/bar-visualizer';
import { Button } from '@/components/ui/button';
import { SmoothWaveform } from '@/components/ui/smooth-waveform';
import { cn } from '@/lib/utils';
import type { VoiceCallControls } from './useVoiceCall';

/**
 * The composer's call control. Idle, a phone button beside the mic; on a
 * call, it expands over the mic into a "Live" pill whose bars follow the
 * caller's voice, and clicking it hangs up.
 */
export function VoiceCallButton({ call }: { call: VoiceCallControls }) {
  const gt = useGT();

  if (call.status === 'idle') {
    const label = gt('Start voice call', {
      context: 'tooltip for the chat voice call button',
    });
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void call.start()}
        className="h-7 w-7 p-0"
        title={label}
        aria-label={label}
      >
        <PhoneIcon className="h-4 w-4 text-stone-500" />
      </Button>
    );
  }

  const live = call.status === 'live';
  const label = gt('End call', {
    context: 'tooltip for the live voice call pill in the chat composer',
  });
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={call.hangUp}
        title={label}
        aria-label={label}
        className={cn(
          'group inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors',
          live
            ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950 dark:text-red-300'
            : 'border-border bg-muted text-muted-foreground',
        )}
      >
        {live ? (
          <BarVisualizer
            aria-hidden
            // Muted: flat bars, since the voice hears nothing.
            source={call.muted ? null : call.micVisual}
            barCount={4}
            minLevel={0.2}
            idle="pulse"
            className="h-3.5 w-4 [--bar-gap:2px] [--bar-width:3px]"
          />
        ) : (
          <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" />
        )}
        <span>
          {live
            ? gt('Live', {
                context: 'label on the chat composer pill during a voice call',
              })
            : gt('Connecting', {
                context: 'label on the chat composer pill while a call starts',
              })}
        </span>
        <XIcon className="h-3 w-3 opacity-60 group-hover:opacity-100" />
      </button>
      {live && <MuteButton call={call} />}
    </span>
  );
}

/** Mutes the caller's mic without hanging up, e.g. to wait or talk aside. */
function MuteButton({ call }: { call: VoiceCallControls }) {
  const gt = useGT();
  const label = call.muted
    ? gt('Unmute', { context: 'tooltip for the voice call mute button' })
    : gt('Mute', { context: 'tooltip for the voice call mute button' });
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={call.toggleMute}
      className={cn(
        'h-7 w-7 p-0',
        call.muted && 'bg-amber-100 text-amber-800 hover:bg-amber-100',
      )}
      title={label}
      aria-label={label}
      aria-pressed={call.muted}
    >
      {call.muted ? (
        <MicrophoneSlashIcon className="h-4 w-4" weight="fill" />
      ) : (
        <MicrophoneIcon className="h-4 w-4 text-stone-500" />
      )}
    </Button>
  );
}

/** Takes the text box's place during a call: the assistant's voice. */
export function VoiceCallWaveform({ call }: { call: VoiceCallControls }) {
  const gt = useGT();
  return (
    <div className="flex min-h-[60px] items-center px-3 py-2">
      <SmoothWaveform
        aria-label={gt('Assistant speaking', {
          context: 'accessible label for the voice call waveform',
        })}
        source={call.assistantVisual}
        loading={call.status !== 'live'}
        className="h-10 w-full text-agent"
      />
    </div>
  );
}
